import React, { useCallback, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import {
  Calendar,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  RefreshCcw,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  fetchAppointments,
  createClinicBooking,
  updateClinicBooking,
  deleteClinicBooking,
} from "../../lib/therapistApi";
import ClientIntakeFormView from "../ClientIntakeFormView";

function useTherapistId() {
  const { therapistId } = useAuth();
  return therapistId;
}

const defaultForm = {
  clientEmail: "",
  clientName: "",
  therapistId: "",
  appointmentDate: "",
  duration: 60,
  notes: "",
  status: "confirmed",
};

const BookingsManagement = () => {
  const therapistId = useTherapistId();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentBookingId, setCurrentBookingId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [intakeFormOpen, setIntakeFormOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const loadBookings = useCallback(async () => {
    if (!therapistId) return;
    try {
      setLoading(true);
      const data = await fetchAppointments(therapistId, {});
      setBookings(data.appointments || []);
    } catch (error) {
      toast.error(error?.message || "Failed to load bookings");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [therapistId]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const handleOpenDialog = (booking = null) => {
    if (booking) {
      setEditMode(true);
      setCurrentBookingId(booking.id || booking._id);
      setForm({
        clientEmail: booking.clientEmail || "",
        clientName: booking.clientName || "",
        therapistId: booking.therapist || therapistId,
        appointmentDate: booking.appointmentDate
          ? format(parseISO(booking.appointmentDate), "yyyy-MM-dd'T'HH:mm")
          : "",
        duration: booking.duration || 60,
        notes: booking.notes || "",
        status: booking.status || "confirmed",
      });
    } else {
      setEditMode(false);
      setCurrentBookingId(null);
      setForm({ ...defaultForm, therapistId });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setForm(defaultForm);
    setEditMode(false);
    setCurrentBookingId(null);
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.clientEmail || !form.clientName || !form.appointmentDate) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        clientEmail: form.clientEmail,
        clientName: form.clientName,
        therapistId: form.therapistId || therapistId,
        appointmentDate: new Date(form.appointmentDate).toISOString(),
        duration: Number(form.duration),
        notes: form.notes,
        status: form.status,
      };

      if (editMode && currentBookingId) {
        await updateClinicBooking(currentBookingId, payload);
        toast.success("Booking updated successfully");
      } else {
        await createClinicBooking(payload);
        toast.success("Booking created successfully");
      }

      await loadBookings();
      handleCloseDialog();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to save booking",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm("Are you sure you want to delete this booking?"))
      return;

    try {
      await deleteClinicBooking(bookingId);
      toast.success("Booking deleted successfully");
      await loadBookings();
    } catch (error) {
      toast.error(error?.message || "Failed to delete booking");
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      confirmed: "default",
      pending: "secondary",
      completed: "outline",
      cancelled: "destructive",
    };
    return <Badge variant={variants[status] || "secondary"}>{status}</Badge>;
  };

  const handleViewIntakeForm = (booking) => {
    setSelectedBooking(booking);
    setIntakeFormOpen(true);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            Bookings Management
          </h1>
          <p className="text-slate-600 mt-2">
            Create, edit, and manage client appointments
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={loadBookings}
            disabled={loading}
            className="border-slate-300 text-slate-600 hover:bg-slate-50">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCcw className="h-4 w-4" />
            )}
            <span className="hidden sm:inline ml-2">Refresh</span>
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => handleOpenDialog()}
                className="gap-2 bg-indigo-600 hover:bg-indigo-700">
                <Plus className="h-4 w-4" />
                New Booking
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>
                  {editMode ? "Edit Booking" : "Create New Booking"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="clientName">Client Name *</Label>
                  <Input
                    id="clientName"
                    value={form.clientName}
                    onChange={(e) => handleChange("clientName", e.target.value)}
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clientEmail">Client Email *</Label>
                  <Input
                    id="clientEmail"
                    type="email"
                    value={form.clientEmail}
                    onChange={(e) =>
                      handleChange("clientEmail", e.target.value)
                    }
                    placeholder="client@example.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="appointmentDate">
                    Appointment Date & Time *
                  </Label>
                  <Input
                    id="appointmentDate"
                    type="datetime-local"
                    value={form.appointmentDate}
                    onChange={(e) =>
                      handleChange("appointmentDate", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="duration">Duration (minutes)</Label>
                  <Input
                    id="duration"
                    type="number"
                    min="15"
                    step="15"
                    value={form.duration}
                    onChange={(e) => handleChange("duration", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={form.status}
                    onValueChange={(value) => handleChange("status", value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={form.notes}
                    onChange={(e) => handleChange("notes", e.target.value)}
                    placeholder="Add any special notes..."
                    rows={3}
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseDialog}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving && (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    )}
                    {editMode ? "Update" : "Create"} Booking
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Calendar className="h-5 w-5 text-indigo-500" />
            All Bookings ({bookings.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
            </div>
          ) : bookings.length > 0 ? (
            <div className="space-y-3">
              {bookings.map((booking) => (
                <div
                  key={booking.id || booking._id}
                  className="border rounded-lg p-4 hover:border-indigo-400 transition">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">
                          {booking.clientName}
                        </p>
                        {getStatusBadge(booking.status)}
                      </div>
                      <p className="text-sm text-gray-600">
                        {booking.clientEmail}
                      </p>
                      <p className="text-sm text-gray-600">
                        {format(
                          parseISO(booking.appointmentDate),
                          "PPP 'at' p",
                        )}{" "}
                        ({booking.duration} min)
                      </p>
                      {booking.notes && (
                        <p className="text-sm text-gray-500 italic">
                          {booking.notes}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewIntakeForm(booking)}
                        className="gap-1">
                        <FileText className="h-3 w-3" />
                        Intake Form
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenDialog(booking)}
                        className="gap-1">
                        <Pencil className="h-3 w-3" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(booking.id || booking._id)}
                        className="gap-1">
                        <Trash2 className="h-3 w-3" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-gray-500">
              <Calendar className="h-6 w-6 text-indigo-400" />
              <p>
                No bookings found. Create your first booking to get started.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Intake Form Dialog */}
      {selectedBooking && (
        <ClientIntakeFormView
          bookingId={selectedBooking.id || selectedBooking._id}
          clientEmail={selectedBooking.clientEmail}
          clientId={selectedBooking.clientId}
          open={intakeFormOpen}
          onOpenChange={setIntakeFormOpen}
        />
      )}
    </div>
  );
};

export default BookingsManagement;
